import chargeTransactionApi from 'api/chargeTransactionApi';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import ImageView from 'general/components/quybka/Images/ImageView';
import KTFormGroup from 'general/components/OtherKeenComponents/Forms/KTFormGroup';
import KTFormInput, { KTFormInputType } from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import AppColors from 'general/constants/AppColors';
import miscHelper from 'general/helpers/miscHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

const ODO_INPUT_NAME = 'chargeTransactionOdo';
const ODO_IMAGE_INPUT_NAME = 'chargeTransactionOdoImage';

DialogEditChargeTransactionOdoView.propTypes = {
  transactionId: PropTypes.string.isRequired,
  initialOdo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialOdoImage: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onUpdated: PropTypes.func.isRequired,
};

function getInitialOdoImagePath(odoImage) {
  if (!odoImage) return '';
  if (Array.isArray(odoImage)) {
    const first = odoImage.find((item) => Utils.isValid(item));
    return first ? String(first) : '';
  }
  return Utils.isValid(odoImage) ? String(odoImage) : '';
}

function getFileNameFromPath(filePath) {
  if (!filePath) return '';
  return filePath.split('/').pop() || filePath;
}

function formatInitialOdo(odo) {
  if (odo == null || odo === '') return '';
  const num = Number(odo);
  if (Number.isNaN(num)) return String(odo);
  return Utils.formatNumber(num);
}

function parseOdoInput(str) {
  const raw = String(str).trim().replace(/\s/g, '').replace(',', '.');
  if (!raw) return NaN;
  if (/^\d{1,3}(\.\d{3})+$/.test(raw)) {
    return parseFloat(raw.replace(/\./g, ''));
  }
  return parseFloat(raw);
}

function DialogEditChargeTransactionOdoView({
  transactionId,
  initialOdo,
  initialOdoImage,
  onUpdated,
}) {
  const fileInputRef = useRef(null);
  const [odoInput, setOdoInput] = useState(() => formatInitialOdo(initialOdo));
  const [existingOdoImagePath, setExistingOdoImagePath] = useState(() =>
    getInitialOdoImagePath(initialOdoImage)
  );
  const [odoImageFile, setOdoImageFile] = useState(null);
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const el = document.getElementById(ODO_INPUT_NAME);
      if (!el || typeof el.focus !== 'function') return;
      el.focus({ preventScroll: true });
      if (el.value && typeof el.select === 'function') {
        el.select();
      }
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  const parsedOdo = parseOdoInput(odoInput);
  const isValidOdo = Utils.isValid(odoInput) && !Number.isNaN(parsedOdo) && parsedOdo >= 0;
  const hasOdoImage = Boolean(odoImageFile || existingOdoImagePath);
  const showImageError = touched && !hasOdoImage;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOdoImageFile(file);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (!isValidOdo) {
      ToastHelper.showError('Vui lòng nhập số ODO');
      return;
    }
    if (!hasOdoImage) {
      ToastHelper.showError('Vui lòng tải lên ảnh ODO');
      return;
    }

    setLoading(true);
    try {
      let odoImage = existingOdoImagePath || null;
      if (odoImageFile) {
        const mediaList = await miscHelper.callApiUploadFile(odoImageFile);
        odoImage = _.isArray(mediaList) && mediaList.length > 0 ? mediaList[0].relativePath : null;
        if (!odoImage) {
          ToastHelper.showError('Tải ảnh ODO thất bại');
          return;
        }
      }

      const res = await chargeTransactionApi.updateOdo(transactionId, { odo: parsedOdo, odoImage });
      const { result, reason } = res ?? {};
      if (result === 'success') {
        ToastHelper.showSuccess('Cập nhật số ODO thành công');
        onUpdated();
      } else {
        ToastHelper.showError(reason || 'Cập nhật số ODO thất bại');
      }
    } catch (error) {
      const reason = error?.response?.data?.reason;
      ToastHelper.showError(reason || 'Cập nhật số ODO thất bại');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="p-4">
        <KTFormGroup
          label={UIHelper.buildFormLabel('Số ODO', true)}
          inputName={ODO_INPUT_NAME}
          inputElement={
            <KTFormInput
              name={ODO_INPUT_NAME}
              type={KTFormInputType.text}
              value={odoInput}
              onChange={(v) => setOdoInput(v)}
              placeholder="Nhập số ODO"
              unit="Km"
              showUnit
              enableCheckValid
              isValid={isValidOdo}
              isTouched={touched}
              feedbackText={!isValidOdo ? 'Vui lòng nhập số ODO hợp lệ' : ''}
            />
          }
        />
        <div className="mt-4">
          {UIHelper.buildFormLabel('Ảnh ODO', true)}
          <div className="mt-2">
            <input
              ref={fileInputRef}
              id={ODO_IMAGE_INPUT_NAME}
              name={ODO_IMAGE_INPUT_NAME}
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              hidden
              onChange={handleFileChange}
              onClick={(e) => {
                e.target.value = '';
              }}
            />
            {hasOdoImage ? (
              <div style={{ maxWidth: '240px' }}>
                <ImageView
                  name={ODO_IMAGE_INPUT_NAME}
                  path={odoImageFile ? odoImageFile.name : existingOdoImagePath}
                  item={odoImageFile || existingOdoImagePath}
                  fileName={
                    odoImageFile
                      ? odoImageFile.name
                      : getFileNameFromPath(existingOdoImagePath)
                  }
                  onRemove={() => {
                    setOdoImageFile(null);
                    setExistingOdoImagePath('');
                  }}
                />
              </div>
            ) : (
              <button
                type="button"
                className="btn d-inline-flex align-items-center"
                style={{
                  backgroundColor: AppColors.gray[200],
                  borderRadius: '6px',
                  padding: '10px 16px',
                  border: 'none',
                  ...UIHelper.buildFontStyle('500', '14px', AppColors.gray[800]),
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="fa-regular fa-upload mr-2" style={{ fontSize: 14 }} />
                Tải lên ảnh ODO
              </button>
            )}
          </div>
        </div>
      </div>
      <div
        className="d-flex align-items-center justify-content-end gap-3 px-4 py-3"
        style={{ borderTop: `1px solid ${AppColors.gray[200]}` }}
      >
        {UIHelper.buildNegativeButton('Hủy bỏ', () => AppCustomDialogHelper.hide(), null)}
        {UIHelper.buildGreenButton('Cập nhật', handleSubmit, null, false, loading)}
      </div>
    </div>
  );
}

export default DialogEditChargeTransactionOdoView;
